import { getPageContentByDeliveryKey } from '@api/getPageContent';
import { GeoLang } from '@repo/ui/src/types/geo';
import { appFetchAsyncFuncs } from '@utils/appComponentDirectory';

// Shared interfaces for function parameters
interface BaseProps {
  geo: GeoLang;
  pageNumber?: string;
  deliveryKey?: string;
  preview?: boolean;
}

interface LoadAsyncDataProps extends BaseProps {
  props: any;
}

// Breakdown Amplience Data into array to pass into getComponent function used for page level slots

const loadAsyncData = async ({
  props,
  geo,
  pageNumber,
  deliveryKey,
  preview,
}: LoadAsyncDataProps): Promise<void> => {
  if (!props?._meta?.schema || !geo) return;

  if (props.components) {
    // Ensure any async data for Split Background component is loaded
    props.body = props.components;
  }

  // Load async data for any children
  if (props?.body?.length) {
    await Promise.all(
      props.body.map((x: any) =>
        loadAsyncData({
          props: x,
          geo,
          pageNumber,
          deliveryKey,
          preview,
        }),
      ),
    );
  }

  // find the component in the directory, call its fetchAsync method,
  // and pass the results to the component in a _data prop
  const fetchAsyncImport =
    appFetchAsyncFuncs[props._meta.schema as keyof typeof appFetchAsyncFuncs];
  if (fetchAsyncImport) {
    const { default: fetchAsync } = await fetchAsyncImport();
    props._data = (await fetchAsync({ ...props, geo, pageNumber, deliveryKey, preview })) ?? null;
  }
};

interface LoadAsyncDataWithContentRowsProps extends BaseProps {
  contentType: any;
}
export const loadAsyncDataWithContentRows = async ({
  contentType,
  geo,
  pageNumber,
  deliveryKey,
  preview,
}: LoadAsyncDataWithContentRowsProps): Promise<void> => {
  if (!contentType || !geo) return;

  await loadAsyncData({
    props: contentType,
    geo,
    pageNumber,
    deliveryKey,
    preview,
  });

  if (!contentType.contentRows?.components?.length) return;

  // For every component in contentRows, load any async data
  await Promise.all(
    contentType.contentRows.components.map((x: any) =>
      loadAsyncData({
        props: x,
        geo,
        pageNumber,
        deliveryKey,
        preview,
      }),
    ),
  );
};

interface GetAndBreakdownDetailPageProps extends BaseProps {
  contentType: any;
}

export const getAndBreakdownDetailPage = async ({
  contentType,
  geo,
  pageNumber,
  deliveryKey,
  preview,
}: GetAndBreakdownDetailPageProps): Promise<any> => {
  const response = await getPageContentByDeliveryKey(geo.generalPage, preview);
  if (response) {
    const components = await breakdownResponse({
      body: response,
      geo,
      pageNumber,
      deliveryKey,
      preview,
    });
    return (
      await Promise.all(
        components.map(async (component: any) => {
          if (component.schema === 'https://traegergrills.com/data/nestedSlot/generalBody.json') {
            await loadAsyncDataWithContentRows({
              contentType,
              geo,
              pageNumber,
              deliveryKey,
              preview,
            });
            if (!component) {
              return null;
            }
            return {
              ...component,
              props: {
                ...component.props,
                body: [
                  {
                    schema: contentType._meta.schema ?? null,
                    id: contentType._meta.deliveryId ?? null,
                    props: contentType ?? null,
                  },
                ],
              },
            };
          }
          return component;
        }),
      )
    ).filter(Boolean);
  }
};

interface BreakdownResponseProps extends BaseProps {
  body?: { body?: any; _meta?: any };
}
const breakdownResponse = async ({
  body = {},
  geo,
  pageNumber,
  deliveryKey,
  preview,
}: BreakdownResponseProps): Promise<any> => {
  if (Array.isArray(body?.body)) {
    return (
      await Promise.all(
        body.body.map(async (child: any) => {
          if (child._meta) {
            await loadAsyncData({
              props: child,
              geo,
              pageNumber,
              deliveryKey,
              preview,
            });
            return {
              schema: child._meta.schema ?? null,
              id: child._meta.deliveryId ?? null,
              props: child ?? null,
            };
          } else {
            console.warn(
              `Child Content: ${child} does not contain _meta property, can not be mapped`,
            );
          }
        }),
      )
    ).filter(Boolean);
  } else if (body?._meta) {
    return getAndBreakdownDetailPage({
      contentType: body,
      geo,
      pageNumber,
      deliveryKey,
      preview,
    });
  }
};

// Breakdown Data inside of a slot body component, used for nested slots
export const breakdownChildResponse = (res: any): any => {
  if (Array.isArray(res)) {
    return res
      .filter((child: any) => {
        if (!child?._meta) {
          console.warn(`Child Content ${child} does not contain _meta property, can not be mapped`);
          return false;
        }
        return true;
      })
      .map((child: any) => ({
        schema: child._meta.schema,
        id: child._meta.deliveryId,
        props: child,
      }));
  } else {
    console.warn(`Content: ${res} is not formatted correctly.`);
  }
};

export default breakdownResponse;
